import React, {FC, useEffect, useRef, useState} from 'react'

type IProps = {
  value: any
  setValue: any
  onChange: (id: string, value: string, type: string) => void
  id: string
  label: string
  type?: string
  options?: {value: string; label: string}[]
  // How long after the last keystroke the value is reported. Screens that fetch on every change
  // keep the default; screens that only stage the value until Filter pass 0 and get it at once.
  delay?: number
}

const TextTableFilter: FC<IProps> = ({label, id, onChange, type, value, setValue, options, delay = 1000}) => {
  const [inputState, setInputState] = useState(value);
  // One timer per field, so one field's typing delay can't cancel another's.
  const typingTimer = useRef<ReturnType<typeof setTimeout>>()
  const pendingValue = useRef<string | null>(null)
  // The timer reports through the latest onChange, not the one from the render the keystroke
  // happened in, so it never writes into filters the page has already replaced.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(()=>{
    if (inputState !== value) {
      setInputState(value)
    }
  },[value])

  const cancelTimer = () => {
    if (typingTimer.current !== undefined) {
      clearTimeout(typingTimer.current)
    }
  }

  useEffect(() => cancelTimer, [])

  const reportPending = () => {
    cancelTimer()
    if (pendingValue.current === null) {
      return
    }
    const pending = pendingValue.current
    pendingValue.current = null
    onChangeRef.current(id, pending, type || 'text')
  }

  const handleChange = (e: React.ChangeEvent<any>) => {
    if (type === 'select') {
      onChange(id, e.target.value, type)
      setInputState(e.target.value)
      setValue(e.target.value)
    } else {

      cancelTimer()

      setValue(e.target.value)
      setInputState(e.target.value)

      pendingValue.current = e.target.value
      if (delay === 0) {
        reportPending()
      } else {
        typingTimer.current = setTimeout(reportPending, delay)
      }
    }
  }

  return (
    <div className='col-sm-12 col-md-3 mb-5'>
      <label className='form-label fw-bold'>{label}</label>
      <div>
        {type && type === 'select' ? (
          <select className='form-control' id={id} value={inputState} onChange={handleChange}>
            {options &&
              options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
          </select>
        ) : (
          <input
            className='form-control'
            type={type || 'text'}
            id={id}
            value={inputState}
            onChange={handleChange}
            // Leaving the field (e.g. to click Filter) reports what it shows at once, so Filter
            // never applies a value that is still waiting out the typing delay.
            onBlur={reportPending}
          />
        )}
      </div>
    </div>
  )
}

export default TextTableFilter
